import React from 'react'
import { Link, useHistory } from 'react-router-dom'

import { useLazyQuery } from '@apollo/client'
import {
  AppBar,
  Avatar,
  Fade,
  Grid,
  Hidden,
  InputBase,
  Typography,
  Zoom
} from '@material-ui/core'
import { useNProgress } from '@tanem/react-nprogress'
import { isAfter } from 'date-fns'

import { UserContext } from '../../App'
import { SEARCH_USERS } from '../../graphql/queries'
import {
  AddIcon,
  ExploreActiveIcon,
  ExploreIcon,
  HomeActiveIcon,
  HomeIcon,
  LikeActiveIcon,
  LikeIcon,
  LoadingIcon
} from '../../icons'
import logo from '../../images/logo.png'
import { RedTooltip, useNavbarStyles, WhiteTooltip } from '../../styles'
import NotificationList from '../notification/NotificationList'
import NotificationTooltip from '../notification/NotificationTooltip'
import AddPostDialog from '../post/AddPostDialog'

function Navbar({ minimalNavbar }) {
  const classes = useNavbarStyles()
  const history = useHistory()
  const path = history.location.pathname
  const [isLoadingPage, setLoadingPage] = React.useState(true)

  React.useEffect(() => {
    setTimeout(() => {
      setLoadingPage(false)
    }, 350)
  }, [path])

  return (
    <>
      <AppBar className={classes.appBar}>
        <section className={classes.section}>
          <Logo />
          {!minimalNavbar && (
            <>
              <Search history={history} />
              <Links path={path} />
            </>
          )}
        </section>
      </AppBar>
      <Progress isAnimating={isLoadingPage} />
    </>
  )
}

function Logo() {
  const classes = useNavbarStyles()

  return (
    <div className={classes.logoContainer}>
      <Link to="/">
        <div className={classes.logoWrapper}>
          <img src={logo} alt="Instagram" className={classes.logo} />
        </div>
      </Link>
    </div>
  )
}

function Search({ history }) {
  const classes = useNavbarStyles()
  const [results, setResults] = React.useState([])
  const [query, setQuery] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [searchUsers, { data }] = useLazyQuery(SEARCH_USERS)

  const hasResults = Boolean(query) && results.length > 0

  React.useEffect(() => {
    if (!query.trim()) return
    setLoading(true)

    const variables = {
      query: `%${query}%`
    }

    searchUsers({ variables })
    if (data) {
      setResults(data.users)
    }
    setLoading(false)
  }, [query, data, searchUsers])

  function handleClearInput() {
    setQuery('')
  }

  return (
    <Hidden xsDown>
      <WhiteTooltip
        arrow
        interactive
        TransitionComponent={Fade}
        open={hasResults}
        title={
          hasResults && (
            <Grid className={classes.resultContainer} container>
              {results.map(result => (
                <Grid
                  key={result.id}
                  item
                  className={classes.resultLink}
                  onClick={() => {
                    history.push(`/${result.username}`)
                    handleClearInput()
                  }}
                >
                  <div className={classes.resultWrapper}>
                    <div className={classes.avatarWrapper}>
                      <Avatar src={result.profile_image} alt="User avatar" />
                    </div>
                    <div className={classes.nameWrapper}>
                      <Typography variant="body2" color="textSecondary">
                        {result.name}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          )
        }
      >
        <InputBase
          className={classes.input}
          onChange={event => setQuery(event.target.value)}
          startAdornment={<span className={classes.searchIcon} />}
          endAdornment={
            loading ? (
              <LoadingIcon />
            ) : (
              <span onClick={handleClearInput} className={classes.clearIcon} />
            )
          }
          placeholder="Search"
          value={query}
        />
      </WhiteTooltip>
    </Hidden>
  )
}

function Links({ path }) {
  const { me, currentUserId } = React.useContext(UserContext)
  const newNotifications = me.notifications.filter(({ created_at }) =>
    isAfter(new Date(created_at), new Date(me.last_checked))
  )
  const hasNotifications = newNotifications.length > 0
  const classes = useNavbarStyles()
  const [showList, setShowList] = React.useState(false)
  const [showTooltip, setShowTooltip] = React.useState(hasNotifications)
  const [media, setMedia] = React.useState(null)
  const [showAddPostDialog, setAddPostDialog] = React.useState(false)
  const inputRef = React.createRef()

  React.useEffect(() => {
    const timeout = setTimeout(handleHideTooltip, 5000)
    return () => {
      clearTimeout(timeout)
    }
  }, [])

  function handleToggleList() {
    setShowList(prev => setShowList(!prev))
  }

  function handleHideTooltip() {
    setShowTooltip(false)
  }

  function handleHideList() {
    setShowList(false)
  }

  function openFileDialog() {
    inputRef.current.click()
  }

  function handleAddPost(event) {
    setMedia(event.target.files[0])
    setAddPostDialog(true)
  }

  function handleClose() {
    setAddPostDialog(false)
  }

  return (
    <div className={classes.linksContainer}>
      {showList && (
        <NotificationList
          notifications={me.notifications}
          handleHideList={handleHideList}
          currentUserId={currentUserId}
        />
      )}
      <div className={classes.linksWrapper}>
        {showAddPostDialog && (
          <AddPostDialog media={media} handleClose={handleClose} />
        )}
        <>
          <input
            type="file"
            style={{ display: 'none' }}
            ref={inputRef}
            onChange={handleAddPost}
          />
          <AddIcon onClick={openFileDialog} />
        </>
        <Link to="/">{path === '/' ? <HomeActiveIcon /> : <HomeIcon />}</Link>
        <Link to="/explore">
          {path === '/explore' ? <ExploreActiveIcon /> : <ExploreIcon />}
        </Link>
        <RedTooltip
          arrow
          open={showTooltip}
          onOpen={handleHideTooltip}
          TransitionComponent={Zoom}
          title={<NotificationTooltip notifications={newNotifications} />}
        >
          <div
            className={hasNotifications && classes.notifications}
            onClick={handleToggleList}
          >
            {showList ? <LikeActiveIcon /> : <LikeIcon />}
          </div>
        </RedTooltip>
        <Link to={`/${me.username}`}>
          <div
            className={path === `/${me.username}` ? classes.profileActive : ''}
          ></div>
          <Avatar src={me.profile_image} className={classes.profileImage} />
        </Link>
      </div>
    </div>
  )
}

function Progress({ isAnimating }) {
  const classes = useNavbarStyles()
  const { animationDuration, isFinished, progress } = useNProgress({
    isAnimating
  })

  return (
    <div
      className={classes.progressContainer}
      style={{
        opacity: isFinished ? 0 : 1,
        transition: `opacity ${animationDuration}ms linear`
      }}
    >
      <div
        className={classes.progressBar}
        style={{
          marginLeft: `${(-1 + progress) * 100}%`,
          transition: `margin-left ${animationDuration}ms linear`
        }}
      >
        <div className={classes.progressBackground} />
      </div>
    </div>
  )
}

export default Navbar
